/**
 * ============================================================================
 * usePoliciesQuery — Phase 6a frontend (T-P6a-W3-01)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for listing retention policies that drive the
 *          records-disposition workflow.
 * Endpoint: GET /api/v1/retention/policies
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export type PolicyStatus = 'PENDING' | 'ACTIVE' | 'DEPRECATED';

export type RetentionFormula =
  | 'FIXED_YEARS'
  | 'YEARS_AFTER_PRODUCT_DISCONTINUATION'
  | 'YEARS_AFTER_EXPIRY'
  | 'INDEFINITE';

export type DataClass = 'GMP' | 'GLP' | 'GCP' | 'GDP' | 'NON_GXP';

export type RetentionAnchor =
  | 'EFFECTIVE_DATE'
  | 'SUPERSEDED_DATE'
  | 'PRODUCT_DISCONTINUATION'
  | 'BATCH_EXPIRY'
  | 'LAST_USE';

export interface RetentionPolicy {
  policy_id: string;
  name: string;
  doc_type_code: string;
  formula: RetentionFormula;
  formula_years: number | null;
  data_class: DataClass;
  anchors: RetentionAnchor[];
  status: PolicyStatus;
  created_at_utc: string;
  activated_at_utc: string | null;
  deprecated_at_utc: string | null;
  version: number;
  signature_bundle_ids?: string[];
}

export interface PoliciesResponse {
  items: RetentionPolicy[];
}

/**
 * Mock-api / contract-drift normalizer. The mock-api still returns the
 * V7 draft shape (`retention_policy_id`, `policy_name`, `default_for_doc_type`,
 * `applies_to_data_class`, `regulatory_anchors`) — the screen was rewritten
 * against the post-V7 contract (`policy_id`, `name`, `doc_type_code`,
 * `data_class`, `anchors`). Coerce here so the grid renders.
 */
function normalizePolicies(raw: unknown): PoliciesResponse {
  const items = Array.isArray((raw as any)?.items) ? (raw as any).items : [];
  const mapped: RetentionPolicy[] = items.map((p: any) => ({
    policy_id:        p.policy_id        ?? p.retention_policy_id ?? '?',
    name:             p.name             ?? p.policy_name         ?? '(unnamed)',
    doc_type_code:    p.doc_type_code    ?? p.default_for_doc_type ?? '—',
    formula:          p.formula          ?? 'FIXED_YEARS',
    formula_years:    p.formula_years    ?? null,
    data_class:       p.data_class       ?? p.applies_to_data_class ?? 'GMP',
    anchors:          (Array.isArray(p.anchors) ? p.anchors
                       : Array.isArray(p.regulatory_anchors) ? p.regulatory_anchors
                       : []) as RetentionAnchor[],
    status:           p.status           ?? 'ACTIVE',
    created_at_utc:   p.created_at_utc   ?? p.activated_at_utc ?? new Date().toISOString(),
    activated_at_utc: p.activated_at_utc ?? null,
    deprecated_at_utc: p.deprecated_at_utc ?? null,
    version:          p.version          ?? 1,
    signature_bundle_ids: Array.isArray(p.signature_bundle_ids) ? p.signature_bundle_ids : [],
  }));
  return { items: mapped };
}

export function usePoliciesQuery(): UseQueryResult<PoliciesResponse> {
  return useQuery<PoliciesResponse>({
    queryKey: ['retention', 'policies'],
    queryFn: ({ signal }) =>
      apiFetch<unknown>('/api/v1/retention/policies', { signal }).then(normalizePolicies),
  });
}
